import React, { useRef } from 'react';
import { MessageMention } from '../../types';

/**
 * 말풍선 안의 표. 본문(탭 구분 평문)에서 getMessageBlocks가 골라낸 블록만 여기로 온다.
 *
 * ⚠ z-* 를 붙이지 말 것 — 선택 모드 오버레이(MessageItem의 absolute inset-0 z-10)를 뚫고
 *   표가 눌린다. 링크 미리보기 카드가 같은 이유로 z를 쓰지 않는 것과 같은 규칙이다.
 */
export interface MessageTableProps {
  rows: string[][];
  hasHeader: boolean;
  align: ('left' | 'right')[];
  isOwn: boolean;
  darkMode?: boolean;
  mentions?: MessageMention[];
  /**
   * ChatWindow의 highlightText를 그대로 받는다 — 링크·멘션 칩·검색어 하이라이트의 단일 출처.
   * ⚠ 반드시 useCallback으로 고정된 참조여야 한다(아래 React.memo가 이것에 걸려 있다).
   */
  renderCell: (text: string, isOwn?: boolean, mentions?: MessageMention[]) => React.ReactNode;
}

// ⚠ Tailwind JIT는 조립한 클래스명(`bg-brand-${n}`)을 스캔하지 못한다 — 전부 리터럴로 둔다.
//    삼항 순서는 CLAUDE.md 규약대로 isOwn → darkMode → light.
const SURFACE = {
  own: { cell: 'border-brand-300', th: 'bg-white/60 text-slate-800', zebra: 'even:bg-white/25' },
  dark: { cell: 'border-gray-600', th: 'bg-gray-700 text-gray-100', zebra: 'even:bg-gray-700/40' },
  light: { cell: 'border-slate-200', th: 'bg-slate-100 text-slate-700', zebra: 'even:bg-slate-50' },
} as const;

/** 이 길이를 넘는 셀에만 폭 상한 래퍼를 씌운다 (td의 max-width는 무시되므로 안쪽 span이 필요) */
const LONG_CELL = 40;

const MessageTableComponent: React.FC<MessageTableProps> = ({
  rows, hasHeader, align, isOwn, darkMode, mentions, renderCell,
}) => {
  const surface = isOwn ? SURFACE.own : darkMode ? SURFACE.dark : SURFACE.light;
  const head = hasHeader ? rows[0] : null;
  const body = hasHeader ? rows.slice(1) : rows;

  // 가로 스크롤은 JS로 직접 민다. touch-action은 요소부터 스크롤 조상까지 '교집합'으로 결정되는데
  // MessageItem 행이 pan-y라(스와이프 답장의 정공법) 여기서 pan-x를 선언해도 교집합이 none이 되어
  // 네이티브 가로 스크롤이 아예 생기지 않는다 — CSS로는 되돌릴 수 없다.
  const startX = useRef(0);
  const startY = useRef(0);
  const startLeft = useRef(0);
  const axis = useRef<null | 'x' | 'y'>(null);

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    startLeft.current = e.currentTarget.scrollLeft;
    axis.current = null;
    // stopPropagation 하지 않는다 — 표 위에서도 롱프레스 컨텍스트 메뉴가 열려야 한다
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    if (axis.current === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      axis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (axis.current !== 'x') return; // 세로는 목록 네이티브 스크롤에 맡긴다
    // React 루트 리스너가 passive라 preventDefault는 쓸 수 없다. scrollLeft만 직접 옮긴다
    e.currentTarget.scrollLeft = startLeft.current - dx;
  };

  const cellNode = (text: string) => {
    if (text === '') return '\u00a0'; // 빈 셀이 0폭으로 무너지지 않게
    const node = renderCell(text, isOwn, mentions);
    return text.length > LONG_CELL
      ? <span className="block max-w-[14rem]">{node}</span>
      : node;
  };

  return (
    <div
      data-msg-table
      className="my-1 max-w-full overflow-x-auto overscroll-x-contain"
      style={{ touchAction: 'pan-y' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
    >
      {/* w-max: 표가 컨테이너에 맞춰 줄어들지 않고 제 폭을 유지해야 가로 스크롤이 생긴다 */}
      <table className="w-max border-collapse text-xs md:text-sm">
        {head && (
          <thead>
            <tr>
              {head.map((cell, j) => (
                <th
                  key={j}
                  className={`table-cell-text border px-2 py-1 text-left align-top font-semibold ${surface.cell} ${surface.th}`}
                >
                  {cellNode(cell)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {body.map((row, i) => (
            <tr key={i} className={surface.zebra}>
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={`table-cell-text border px-2 py-1 align-top ${surface.cell} ${align[j] === 'right' ? 'text-right' : ''}`}
                >
                  {cellNode(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * rows/align은 messageTable의 모듈 캐시가, renderCell은 ChatWindow의 useCallback이 참조를 고정한다.
 * 하나라도 흔들리면 타이핑 한 글자마다 표 100행이 다시 그려진다.
 */
export const MessageTable = React.memo(MessageTableComponent);
